import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BASEMAP_ADDON_GIS,
  BASEMAP_ESRI_NAVIGATION_DARK,
  BASEMAP_ESRI_NAVIGATION_LIGHT,
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { setBasemap, setMapAddon, setMapProvider } from '@rsos/sinatra';
import { DARK_THEME, LIGHT_THEME } from '@rsos/theme/constants';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import usePortalType from '@rsos/utils/usePortalType';

/**
 * Determine which initial map provider to use on load and store the values in the sinatra.ui state.
 * @returns String
 */
const useMapProvider = () => {
  const dispatch = useDispatch();

  const themeName = useSelectedTheme();

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const isSatelliteBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_ESRI_SATELLITE || basemap === BASEMAP_GOOGLE_HYBRID
    );
  }, [basemap]);

  const { isPremiumUser } = usePortalType();

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const [initialMapProvider, setInitialMapProvider] = useState('');

  const setDefaultMap = useCallback(
    mapProvider => {
      if (mapProvider === ESRI) {
        setInitialMapProvider(ESRI);

        dispatch(setMapProvider(ESRI));

        dispatch(
          setBasemap(
            themeName === LIGHT_THEME
              ? BASEMAP_ESRI_NAVIGATION_LIGHT
              : BASEMAP_ESRI_NAVIGATION_DARK,
          ),
        );

        // Append 'gis' to the 'mapAddons' array since the /gis-services endpoint is fetched after
        // the map provider is set to ESRI. The GIS addon in the basemap menu is only rendered if
        // the authority has GIS addon (cached-basemap service).
        dispatch(setMapAddon(BASEMAP_ADDON_GIS));
      } else if (mapProvider === GOOGLE) {
        setInitialMapProvider(GOOGLE);

        dispatch(setMapProvider(GOOGLE));

        dispatch(setBasemap(BASEMAP_GOOGLE_ROADMAP));
      }
    },
    [dispatch, themeName],
  );

  // Initialize the map provider or reconcile persisted map state.
  useEffect(() => {
    // The map provider may already be populated from persisted UI state.
    if (mapProvider) {
      setInitialMapProvider(mapProvider);

      // Reconcile persisted Esri state with current access: fall back to Google when Esri is
      // unavailable, or restore the GIS addon when it is missing during initial load.
      if (mapProvider === ESRI) {
        if (!(isPremiumUser && hasEsriMapAccess)) {
          setDefaultMap(GOOGLE);
        } else if (
          !initialMapProvider &&
          !mapAddons.includes(BASEMAP_ADDON_GIS)
        ) {
          dispatch(setMapAddon(BASEMAP_ADDON_GIS));
        }
      }
    } else if (isPremiumUser && hasEsriMapAccess) {
      setDefaultMap(ESRI);
    } else {
      setDefaultMap(GOOGLE);
    }
  }, [
    dispatch,
    hasEsriMapAccess,
    initialMapProvider,
    isPremiumUser,
    mapAddons,
    mapProvider,
    setDefaultMap,
  ]);

  // On page load, ensure Esri basemap matches the theme.
  useEffect(() => {
    if (!initialMapProvider && mapProvider === ESRI && !isSatelliteBasemap) {
      if (
        themeName === LIGHT_THEME &&
        basemap !== BASEMAP_ESRI_NAVIGATION_LIGHT
      ) {
        dispatch(setBasemap(BASEMAP_ESRI_NAVIGATION_LIGHT));
      } else if (
        themeName === DARK_THEME &&
        basemap !== BASEMAP_ESRI_NAVIGATION_DARK
      ) {
        dispatch(setBasemap(BASEMAP_ESRI_NAVIGATION_DARK));
      }
    }
  }, [
    basemap,
    dispatch,
    initialMapProvider,
    isSatelliteBasemap,
    mapProvider,
    themeName,
  ]);

  return initialMapProvider;
};

export default useMapProvider;
